import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Lock, Unlock, Play, Coins, Crown } from 'lucide-react';
import api from '../../lib/api';

const videoEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
};

export const ExclusiveContentFeed = ({ posts, myPoints, isSubscriber, isAuthenticated, onRefresh, onSubscribeClick }) => {
  const [unlocking, setUnlocking] = useState(null);

  const unlock = async (post) => {
    if (!isAuthenticated) return toast.info('Iniciá sesión para desbloquear contenido');
    setUnlocking(post.id);
    try {
      await api.post(`/fullbravx/content/${post.id}/unlock`);
      toast.success('¡Contenido desbloqueado!');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo desbloquear');
    } finally { setUnlocking(null); }
  };

  if (!posts.length) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="exclusive-content-feed">
      {posts.map((post, idx) => {
        const embed = videoEmbed(post.video_url);
        return (
          <motion.article
            key={post.id}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.06 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0C0C0C]"
            data-testid={`exclusive-post-${post.id}`}
          >
            {/* Media */}
            {post.locked ? (
              <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[#1a1408] via-[#0d0b06] to-black">
                {post.image_url && (
                  <img src={post.image_url} alt="" className="w-full h-full object-cover blur-[45px] scale-150 opacity-50 saturate-50" draggable={false} />
                )}
                <div className="absolute inset-0 bg-black/45 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/60 flex items-center justify-center shadow-[0_0_35px_rgba(212,175,55,0.35)] animate-pulse">
                    <Lock size={24} className="text-[#D4AF37]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.35em] text-white/60 font-black">Contenido bloqueado</span>
                </div>
              </div>
            ) : embed ? (
              <div className="aspect-video">
                <iframe src={embed} title={post.title} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              </div>
            ) : post.video_url ? (
              <video src={post.video_url} controls className="w-full aspect-video object-cover bg-black" poster={post.image_url || undefined} />
            ) : post.image_url ? (
              <img src={post.image_url} alt={post.title} className="w-full aspect-video object-cover" />
            ) : null}

            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[9px] font-black uppercase tracking-widest">
                  <Crown size={9} /> Exclusivo
                </span>
                {!post.locked && <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] font-bold uppercase"><Unlock size={10} /> Desbloqueado</span>}
                {post.video_url && !post.locked && <span className="inline-flex items-center gap-1 text-white/40 text-[10px]"><Play size={10} /> Video</span>}
              </div>
              <h3 className="text-white font-black text-lg leading-tight mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>{post.title}</h3>

              {post.locked ? (
                <>
                  {post.preview && <p className="text-white/40 text-sm leading-relaxed mb-4">{post.preview}</p>}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={onSubscribeClick}
                      className="flex-1 px-4 py-2.5 rounded-full bg-[#D4AF37] text-black text-xs font-black uppercase tracking-widest hover:bg-[#F2D272] transition-colors"
                      data-testid={`post-subscribe-btn-${post.id}`}
                    >
                      Ver con FULL BRAVX
                    </button>
                    {post.unlock_points > 0 && (
                      <button
                        onClick={() => unlock(post)}
                        disabled={unlocking === post.id}
                        className={`flex-1 px-4 py-2.5 rounded-full border text-xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors ${myPoints >= post.unlock_points ? 'border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10' : 'border-white/15 text-white/30 cursor-not-allowed'}`}
                        data-testid={`post-unlock-btn-${post.id}`}
                        title={myPoints < post.unlock_points ? `Te faltan ${post.unlock_points - myPoints} puntos` : ''}
                      >
                        <Coins size={12} /> Desbloquear · {post.unlock_points.toLocaleString()} pts
                      </button>
                    )}
                  </div>
                </>
              ) : (
                post.body && <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line">{post.body}</p>
              )}
            </div>
          </motion.article>
        );
      })}
    </div>
  );
};
